import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

function parseLocation(loc) {
  if (!loc) return { state: '', city: '' };
  if (loc === 'Remote') return { state: 'Remote', city: '' };
  const parts = loc.split('·').map(p => p.trim());
  return { state: parts[0] || '', city: parts[1] || '' };
}

function autoFitColumns(ws, data, startRow) {
  if (!data || data.length === 0) return;
  const range = XLSX.utils.decode_range(ws['!ref']);
  const cols = [];
  for (let c = range.s.c; c <= range.e.c; c++) {
    let maxLen = 8;
    for (let r = range.s.r; r <= range.e.r; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.v != null) {
        const len = String(cell.v).length;
        if (len > maxLen) maxLen = len;
      }
    }
    cols.push({ wch: Math.min(maxLen + 4, 60) });
  }
  ws['!cols'] = cols;
}

function applyMerges(ws, rows, mergeColCount) {
  // Merge cells for columns 0..mergeColCount-1 where the value is empty (continuation of group)
  const merges = [];
  const range = XLSX.utils.decode_range(ws['!ref']);
  const headerRow = range.s.r; // row 0 = header

  for (let c = 0; c < mergeColCount; c++) {
    let startR = headerRow + 1; // first data row
    for (let r = headerRow + 2; r <= range.e.r; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      const val = cell ? cell.v : '';
      if (val !== '' && val != null) {
        // End of merge group
        if (r - 1 > startR) {
          merges.push({ s: { r: startR, c }, e: { r: r - 1, c } });
        }
        startR = r;
      }
    }
    // Final group
    if (range.e.r > startR) {
      merges.push({ s: { r: startR, c }, e: { r: range.e.r, c } });
    }
  }

  ws['!merges'] = merges;
}

// Owner export — full data with merged cells, serial number, separate state/city
export function exportToExcel(filteredCompanies, filteredRoles, contacts, userName, options = {}) {
  const { showDates = true } = options;
  const rows = [];
  let serialNo = 0;

  filteredCompanies.forEach(company => {
    const companyRoles = filteredRoles.filter(r => r.companyId === company.id);
    serialNo++;
    let isFirstRow = true;

    if (companyRoles.length === 0) {
      const { state, city } = parseLocation('');
      const row = { 'S.No': serialNo, 'Company': company.name, 'Company Notes': company.notes || '', 'Role': '', 'Status': '', 'State': state, 'City': city, 'Update Message': '' };
      if (showDates) row['Applied Date'] = '';
      row['Contact Name'] = ''; row['Designation'] = ''; row['Contact Status'] = '';
      rows.push(row);
    } else {
      companyRoles.forEach(role => {
        const roleContacts = contacts.filter(c => c.roleId === role.id);
        const { state, city } = parseLocation(role.location);
        const baseRow = {
          'S.No': isFirstRow ? serialNo : '',
          'Company': isFirstRow ? company.name : '',
          'Company Notes': isFirstRow ? (company.notes || '') : '',
          'Role': role.title,
          'Status': role.status,
          'State': state,
          'City': city,
          'Update Message': role.updateMessage || ''
        };

        if (roleContacts.length === 0) {
          const row = { ...baseRow };
          if (showDates) row['Applied Date'] = role.appliedDate || '';
          row['Contact Name'] = ''; row['Designation'] = ''; row['Contact Status'] = '';
          rows.push(row);
          isFirstRow = false;
        } else {
          let isFirstContact = true;
          roleContacts.forEach(contact => {
            const row = {
              'S.No': isFirstRow ? serialNo : '',
              'Company': isFirstRow ? company.name : '',
              'Company Notes': isFirstRow ? (company.notes || '') : '',
              'Role': isFirstContact ? role.title : '',
              'Status': isFirstContact ? role.status : '',
              'State': isFirstContact ? state : '',
              'City': isFirstContact ? city : '',
              'Update Message': isFirstContact ? (role.updateMessage || '') : ''
            };
            if (showDates) row['Applied Date'] = isFirstContact ? (role.appliedDate || '') : '';
            row['Contact Name'] = contact.name;
            row['Designation'] = contact.designation || '';
            row['Contact Status'] = contact.linkedinStatus || '';
            rows.push(row);
            isFirstRow = false;
            isFirstContact = false;
          });
        }
      });
    }
  });

  if (rows.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(rows);
  autoFitColumns(ws, rows, 0);

  // Apply merges for S.No, Company, Company Notes (first 3 columns)
  applyMerges(ws, rows, 3);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Applications');
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(data, `TT_${userName || 'Export'}_Dashboard.xlsx`);
}

// Third-party export — respects exact permissions, merged cells, serial number
export function exportToExcelPublic(filteredCompanies, filteredRoles, allContacts, userName, permissions, options = {}) {
  const { showDates = true } = options;
  const rows = [];
  let serialNo = 0;

  filteredCompanies.forEach(company => {
    const companyRoles = filteredRoles.filter(r => r.companyId === company.id);
    serialNo++;
    let isFirstRow = true;

    if (companyRoles.length === 0) {
      const row = { 'S.No': serialNo, 'Company': company.name };
      if (permissions.notes) row['Company Notes'] = company.notes || '';
      if (permissions.roles) {
        row['Role'] = ''; row['Status'] = '';
        if (permissions.state) row['State'] = '';
        if (permissions.city) row['City'] = '';
        if (permissions.liveStatus) row['Update Message'] = '';
        if (showDates) row['Applied Date'] = '';
      }
      if (permissions.contacts) { row['Contact Name'] = ''; row['Designation'] = ''; row['Contact Status'] = ''; }
      rows.push(row);
    } else {
      companyRoles.forEach(role => {
        const roleContacts = permissions.contacts ? allContacts.filter(c => c.roleId === role.id) : [];
        const { state, city } = parseLocation(role.location);

        if (roleContacts.length === 0) {
          const row = { 'S.No': isFirstRow ? serialNo : '', 'Company': isFirstRow ? company.name : '' };
          if (permissions.notes) row['Company Notes'] = isFirstRow ? (company.notes || '') : '';
          if (permissions.roles) {
            row['Role'] = role.title; row['Status'] = role.status;
            if (permissions.state) row['State'] = state;
            if (permissions.city) row['City'] = city;
            if (permissions.liveStatus) row['Update Message'] = role.updateMessage || '';
            if (showDates) row['Applied Date'] = role.appliedDate || '';
          }
          if (permissions.notes && permissions.roles) row['Role Notes'] = role.notes || '';
          if (permissions.contacts) { row['Contact Name'] = ''; row['Designation'] = ''; row['Contact Status'] = ''; }
          rows.push(row);
          isFirstRow = false;
        } else {
          let isFirstContact = true;
          roleContacts.forEach(contact => {
            const row = { 'S.No': isFirstRow ? serialNo : '', 'Company': isFirstRow ? company.name : '' };
            if (permissions.notes) row['Company Notes'] = isFirstRow ? (company.notes || '') : '';
            if (permissions.roles) {
              row['Role'] = isFirstContact ? role.title : '';
              row['Status'] = isFirstContact ? role.status : '';
              if (permissions.state) row['State'] = isFirstContact ? state : '';
              if (permissions.city) row['City'] = isFirstContact ? city : '';
              if (permissions.liveStatus) row['Update Message'] = isFirstContact ? (role.updateMessage || '') : '';
              if (showDates) row['Applied Date'] = isFirstContact ? (role.appliedDate || '') : '';
            }
            if (permissions.notes && permissions.roles) row['Role Notes'] = isFirstContact ? (role.notes || '') : '';
            row['Contact Name'] = contact.name; row['Designation'] = contact.designation || ''; row['Contact Status'] = contact.linkedinStatus || '';
            rows.push(row);
            isFirstRow = false;
            isFirstContact = false;
          });
        }
      });
    }
  });

  if (rows.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(rows);
  autoFitColumns(ws, rows, 0);

  // Apply merges for S.No, Company (first 2 columns), plus Company Notes if present
  const mergeColCount = permissions.notes ? 3 : 2;
  applyMerges(ws, rows, mergeColCount);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Applications');
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(data, `TT_${userName || 'Export'}_Status.xlsx`);
}
